import type { NextPage } from 'next'
import Image from 'next/image'

import {
    activityStreamBigImage,
    activityStreamImage,
    commandLineShortcutsImage,
    fieldGroupsEditing3Image,
    tableBulkEditingImage,
    todayStackImage,
    marblesPushedIntoTubeBigImage,
} from 'src/libs/assets'
import { PageLayout } from 'src/libs/layout'
import {
    IntroSection,
    PropositionSection,
    SecuritySection,
    ClosingSection,
    ThreeCardsSection,
    FaqSection,
    BannerWithCtaSection,
    RequestDemoButton,
} from 'src/libs/ui-shared'

const SdrsBdrsSolutionPage: NextPage = () => {
    return (
        <PageLayout isNavBarFixed>
            <div className="grid gap-40">
                <IntroSection
                    image={marblesPushedIntoTubeBigImage}
                    imageAlt="Marbles being pushed into tube"
                    label="SDRs / BDRs"
                    title="Edit records with one click"
                    description="Find your leads, acccounts, opps with without the tedious clicks and make updates in split seconds."
                >
                    <div className="grid place-content-center md:place-content-start">
                        <RequestDemoButton />
                    </div>
                </IntroSection>

                <PropositionSection
                    title="Spend less time in Salesforce"
                    description="Use a modern interface to manage all your leads intuitively and fast. Everything is synced back to Salesforce, so you avoid double work."
                    image={
                        <Image
                            src={fieldGroupsEditing3Image}
                            alt="Editing field in field group"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <PropositionSection
                    title="Update fields in bulk"
                    description="Create custom lists and views with fields that are relevant to your workflow and update all of them withing seconds."
                    image={
                        <Image
                            src={tableBulkEditingImage}
                            alt="Bulk editing in table"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                />

                <PropositionSection
                    title="Eliminate the copy + paste work"
                    description="Laserfocus syncs seamlessly with your emails, calendar and Salesforce records. It’s the one place for your sales activities."
                    image={
                        <Image
                            src={activityStreamBigImage}
                            alt="Acitivty stream"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <BannerWithCtaSection />

                <SecuritySection />

                <ThreeCardsSection
                    title="Remove the friction in your sales day"
                    description="You can see everything you want to work on in an instant. Laserfocus is your one system to organize your day AND contact all your prospects."
                    cards={[
                        {
                            image: (
                                <Image
                                    src={todayStackImage}
                                    alt="Daily overview in Laserfocus app"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Daily overview',
                            description:
                                'See all your tasks, reminders and events. Reschedule them with one-click to save you time.',
                            backgroundColor: 'blue',
                        },
                        {
                            image: (
                                <Image
                                    src={commandLineShortcutsImage}
                                    alt="Command line shortcuts"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Shortcuts to save time',
                            description:
                                'Use shortcuts to set tasks, reminders and logs from anywhere to stay focused on the work at hand.',
                            backgroundColor: 'purple',
                        },
                        {
                            image: (
                                <Image
                                    src={activityStreamImage}
                                    alt="Acitivty stream"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Better handovers',
                            description:
                                'All activities are shown chronologically in Laserfocus, so your team will have everything for a smooth handover.',
                            backgroundColor: 'red',
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus helps you become more productive by offering you all the tools you need to close deals with confidence."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'What does Laserfocus cost?',
                            answer: 'It’s free for individuals and small teams trying out Laserfocus.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <ClosingSection
                    title="Get started for free with 1 Click"
                    description="Laserfocus is build to save you time in your day-to-day. Start now and get more out of your day."
                />
            </div>
        </PageLayout>
    )
}

export default SdrsBdrsSolutionPage
